import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerField } from '@/components/ui/date-picker-field';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Subscription } from './types/subscription';
import { Product } from './types/product';
import { UserRole, ROLE_METADATA } from '@/components/authnz/types/rbac';
import { apiFetch } from '@/lib/api-client';
import { safeToDate } from '@/lib/utils';
import logger from '@/lib/logging';
import { useAdmin } from '@/contexts/AdminContext';
import { productsAssignableByActor } from '@/lib/practitioner-subscription-policy';

const FAR_FUTURE_DATE = new Date(2100, 0, 1);

const formSchema = z.object({
  productId: z.string().min(1, { message: 'Product is required' }),
  userId: z.string().min(1, { message: 'User ID is required' }),
  userEmails: z.string().min(1, { message: 'At least one email is required' }),
  userRole: z.nativeEnum(UserRole),
  isLifetime: z.boolean().optional(),
  currentPeriodEnd: z.date().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface SubscriptionFormProps {
  subscription?: Subscription;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function SubscriptionForm({ subscription, onSuccess, onCancel }: SubscriptionFormProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { isAuthorizedAdmin } = useAdmin();

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const productsData = await apiFetch<Product[]>('/products');
        setProducts(productsAssignableByActor(productsData, isAuthorizedAdmin));
      } catch (error) {
        logger.error('Error loading products:', error);
        toast({
          title: 'Error',
          description: 'Failed to load products. Run Reconcile from Stripe on the Products tab.',
          variant: 'destructive',
        });
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [toast, isAuthorizedAdmin]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      productId: subscription?.productId || '',
      userId: subscription?.userId || '',
      userEmails: subscription?.userEmails.join(', ') || '',
      userRole: subscription?.userRole || UserRole.PRACTITIONER,
      isLifetime: subscription?.isLifetime || false,
      currentPeriodEnd: subscription?.currentPeriodEnd
        ? safeToDate(subscription.currentPeriodEnd) ?? undefined
        : undefined,
    },
  });

  const isLifetime = form.watch('isLifetime');

  const selectedProductId = form.watch('productId');
  const selectedProduct = products.find(p => p.id === selectedProductId);

  // Update userRole when product changes
  useEffect(() => {
    if (selectedProduct && !subscription) {
      form.setValue('userRole', selectedProduct.userRole);
      form.setValue('isLifetime', selectedProduct.isLifetime);
    }
  }, [selectedProduct, form, subscription]);

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      // Parse emails from comma-separated string
      const emails = values.userEmails
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);

      if (emails.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'At least one email is required',
          variant: 'destructive',
        });
        setSubmitting(false);
        return;
      }

      const subscriptionData: Record<string, unknown> = {
        productId: values.productId,
        userId: values.userId,
        userEmails: emails,
        userRole: values.userRole,
        isLifetime: values.isLifetime || false,
      };

      if (!subscription) {
        // Only meaningful on create — forcing these on every edit used to
        // silently reactivate/reset unrelated edits to canceled or expired
        // subscriptions.
        subscriptionData.startDate = new Date();
        subscriptionData.status = 'active';
      }

      if (!values.isLifetime) {
        subscriptionData.currentPeriodEnd = values.currentPeriodEnd ?? null;
      }

      const endpoint = subscription
        ? `/subscriptions/${subscription.id}`
        : '/subscriptions';
      const method = subscription ? 'PUT' : 'POST';

      await apiFetch(endpoint, {
        method,
        body: JSON.stringify(subscriptionData),
      });

      toast({
        title: subscription ? 'Subscription Updated' : 'Subscription Created',
        description: 'Subscription has been saved successfully',
      });

      onSuccess();
    } catch (error: any) {
      logger.error('Error saving subscription:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save subscription',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="productId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={loadingProducts || !!subscription}
              >
                <FormControl>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} - {product.priceAmount ? `$${(product.priceAmount / 100).toFixed(2)}` : 'N/A'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {selectedProduct && (
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-2">Product Details:</p>
            <p className="text-sm text-muted-foreground">{selectedProduct.description}</p>
            <div className="mt-2 flex gap-2">
              <span className="text-xs bg-background px-2 py-1 rounded">
                Role: {ROLE_METADATA[selectedProduct.userRole].displayName}
              </span>
              {selectedProduct.isLifetime && (
                <span className="text-xs bg-background px-2 py-1 rounded">Lifetime</span>
              )}
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="userId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>User ID</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" placeholder="Firebase Auth UID" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="userEmails"
          render={({ field }) => (
            <FormItem>
              <FormLabel>User Emails</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  className="h-24"
                  placeholder="Enter email addresses separated by commas (e.g., user1@example.com, user2@example.com)"
                />
              </FormControl>
              <FormMessage />
              <p className="text-xs text-muted-foreground">
                Multiple emails can be assigned to this subscription. Separate with commas.
              </p>
            </FormItem>
          )}
        />

        {/* Role Display (derived from product) */}
        {selectedProduct && (
          <FormField
            control={form.control}
            name="userRole"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role to Grant</FormLabel>
                <div className="p-3 bg-muted rounded-lg border">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{ROLE_METADATA[field.value].displayName}</span>
                    <span className="text-xs text-muted-foreground">
                      (derived from product)
                    </span>
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {!isLifetime && (
          <FormField
            control={form.control}
            name="currentPeriodEnd"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End Date</FormLabel>
                <FormControl>
                  <DatePickerField
                    value={field.value}
                    onChange={field.onChange}
                    minDate={new Date(2000, 0, 1)}
                    maxDate={FAR_FUTURE_DATE}
                    placeholder="No end date"
                  />
                </FormControl>
                <FormMessage />
                <p className="text-xs text-muted-foreground">
                  Access is revoked automatically once this date passes. Leave blank for no
                  automatic expiration. Extending the date on an expired subscription reactivates it.
                </p>
              </FormItem>
            )}
          />
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving...' : subscription ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Form>
  );
}

